import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@keyslabor/shared/hooks/useBreakPoint.ts';
import { routes } from '@/constants';

// lucide `Smartphone` geometry — a 14x20 rect, rx 2, at x=5 y=2 in a 24x24
// viewBox — filled rather than stroked, plus a screen inset, an earpiece slot
// and a home dot. lucide ships outline-only icons, so the filled treatment the
// design calls for cannot be a plain `lucide-react` import.
const PhoneIcon = () => (
  <svg className="size-[124px]" viewBox="0 0 24 24" aria-hidden="true">
    <rect width="14" height="20" x="5" y="2" rx="2" ry="2" fill="#143980" />
    <rect x="6.6" y="4.6" width="10.8" height="13.6" rx="0.4" fill="#fff" />
    <rect x="10.4" y="3.15" width="3.2" height="0.7" rx="0.35" fill="#fff" />
    <circle cx="12" cy="20.1" r="0.95" fill="#fff" />
  </svg>
);

/**
 * Blocks the interview flow on desktop viewports (>= 1130px) and points the
 * candidate at the link they were texted.
 *
 * Dismissal is stored as the path it was dismissed on, so navigating to another
 * interview page brings the dialog back with no effect and no cleanup.
 *
 * `/answers` is the recording page: `LandscapeBlock`'s `onRecordDisable` only
 * stops an in-flight recording, it doesn't disable the record button, so
 * dismissing this block there would let a desktop user record and submit an
 * answer. There it renders without the divider/Ok button and without an
 * Escape listener at all — it is a hard, undismissible block.
 */
export const DesktopBlock = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isMobile = useIsMobile();
  const [dismissedPath, setDismissedPath] = useState<string | null>(null);
  const okRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  const isAnswersRoute = pathname === routes.answers;
  const isVisible = !isMobile && dismissedPath !== pathname;

  // Keyed on isVisible alone — deliberately not on pathname/isAnswersRoute.
  // A route change while the dialog stays visible (App renders it outside
  // <Routes>, so it survives navigation) must not re-run this: doing so
  // would restore focus to the pre-dialog element and immediately steal it
  // back, a detour a screen reader can announce even though the end state
  // is unaffected. Only a visibility transition should stash/restore.
  useEffect(() => {
    if (!isVisible) return;

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    return () => {
      // The focused control unmounts (or the block hides) on dismissal,
      // route change, or unmount alike — put focus back where it was
      // instead of letting it fall through to <body>.
      const previouslyFocused = previouslyFocusedRef.current;
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    // No focusable control renders in the card on /answers, so focus the
    // card itself (tabIndex={-1}) — that's still enough to land a
    // screen-reader user inside the dialog. Everywhere else, focus Ok.
    if (isAnswersRoute) {
      cardRef.current?.focus();
    } else {
      okRef.current?.focus();
    }

    // ModalConfirm got this from antd; a plain overlay has to do it itself.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // aria-modal="true" claims the rest of the page doesn't exist, so Tab
    // must not be able to leave the card. There is at most one focusable
    // element in it (the Ok button, and none at all on /answers), so simply
    // cancelling Tab's default action is enough to keep focus in place —
    // no need for a general-purpose focus trap.
    const handleTabTrap = (event: KeyboardEvent) => {
      if (event.key === 'Tab') event.preventDefault();
    };
    document.addEventListener('keydown', handleTabTrap);

    // /answers is a hard block: don't wire up Escape to dismiss it at all.
    const handleEscape = isAnswersRoute
      ? null
      : (event: KeyboardEvent) => {
          if (event.key === 'Escape') setDismissedPath(pathname);
        };
    if (handleEscape) document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleTabTrap);
      if (handleEscape) document.removeEventListener('keydown', handleEscape);
    };
  }, [isVisible, pathname, isAnswersRoute]);

  if (!isVisible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="desktop-block-title"
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/45 p-4"
    >
      <div
        ref={cardRef}
        tabIndex={-1}
        className="w-full max-w-[440px] rounded-lg bg-white px-6 pb-6 pt-8 text-center shadow-xl"
      >
        <div className="flex justify-center">
          <PhoneIcon />
        </div>

        <p
          id="desktop-block-title"
          className="mt-5 text-base font-semibold leading-snug text-black"
        >
          {t('desktop_block.title')}
        </p>
        <p className="mt-4 text-base font-semibold leading-snug text-black">
          {t('desktop_block.message')}
        </p>

        {!isAnswersRoute && (
          <>
            <div className="mb-5 mt-6 h-px bg-gray-200" />

            <button
              ref={okRef}
              type="button"
              onClick={() => setDismissedPath(pathname)}
              className="h-12 min-w-[190px] rounded-full bg-[#569DE5] px-8 text-base font-semibold text-white transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-dark-blue"
            >
              {t('desktop_block.button_label')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default DesktopBlock;
